<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import store from '@/store';
import { cookieCopy } from '@/i18n/cookieCopy.mjs';
import { cookieChoice, cookiePreferencesOpen, consentSaving, consentError, openCookiePreferences, saveCookieChoice, syncCookieChoice } from '@/composables/cookieConsent';

const copy = computed(() => cookieCopy[store.getters.getUserLang] || cookieCopy.en);
const categories = reactive({ analytics: false, support: false });
const status = ref('');
watch(cookiePreferencesOpen, open => {
  if (open) { categories.analytics = cookieChoice.value?.analytics === true; categories.support = cookieChoice.value?.support === true; }
});
async function save(values) {
  status.value = '';
  if (await saveCookieChoice({ ...values, locale: store.getters.getUserLang })) status.value = copy.value.saved;
}
onMounted(() => syncCookieChoice());
</script>

<template>
  <section v-if="!cookieChoice && !cookiePreferencesOpen" class="cookie-banner" aria-labelledby="cookie-banner-title" data-testid="cookie-banner">
    <h2 id="cookie-banner-title">{{ copy.title }}</h2>
    <p>{{ copy.description }}</p>
    <div class="cookie-actions">
      <button type="button" :disabled="consentSaving" @click="save({ analytics: false, support: false })">{{ copy.reject }}</button>
      <button type="button" @click="openCookiePreferences">{{ copy.preferences }}</button>
      <button type="button" :disabled="consentSaving" @click="save({ analytics: true, support: true })">{{ copy.accept }}</button>
    </div>
  </section>
  <button v-if="cookieChoice && !cookiePreferencesOpen" class="cookie-reopen" type="button" @click="openCookiePreferences">{{ copy.preferences }}</button>
  <p class="sr-only" aria-live="polite" aria-atomic="true">{{ status }}</p>
  <div v-if="consentError && !cookiePreferencesOpen" class="cookie-error" role="alert">
    {{ copy.pending }} <button type="button" :disabled="consentSaving" @click="syncCookieChoice">{{ copy.retry }}</button>
  </div>
  <Dialog v-model:visible="cookiePreferencesOpen" modal :header="copy.preferences" :draggable="false"
    :style="{ width: '38rem', maxWidth: 'calc(100vw - 2rem)' }" :baseZIndex="20000" :contentStyle="{ overflowY: 'auto' }">
    <div class="cookie-preferences" data-testid="cookie-preferences">
      <p>{{ copy.description }}</p>
      <div class="cookie-category"><strong>{{ copy.necessary }}</strong><p>{{ copy.necessaryHelp }}</p></div>
      <div class="cookie-category">
        <label><input v-model="categories.analytics" type="checkbox" data-testid="consent-analytics" :disabled="consentSaving"> {{ copy.analytics }}</label>
        <p>{{ copy.analyticsHelp }}</p>
      </div>
      <div class="cookie-category">
        <label><input v-model="categories.support" type="checkbox" data-testid="consent-support" :disabled="consentSaving"> {{ copy.support }}</label>
        <p>{{ copy.supportHelp }}</p>
      </div>
      <RouterLink to="/cookies-policy" @click="cookiePreferencesOpen = false">{{ copy.policy }}</RouterLink>
      <p v-if="consentError" role="alert">{{ copy.pending }}</p>
    </div>
    <template #footer>
      <div class="cookie-actions">
        <button type="button" :disabled="consentSaving" @click="save({ analytics: false, support: false })">{{ copy.reject }}</button>
        <button type="button" :disabled="consentSaving" @click="save(categories)">{{ consentSaving ? copy.saving : copy.save }}</button>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.cookie-banner, .cookie-error { position: fixed; z-index: 20000; right: 1rem; bottom: 1rem; width: min(40rem, calc(100vw - 2rem)); max-height: calc(100dvh - 2rem); overflow: auto; padding: 1.25rem; background: #fff; color: #10234a; border: 1px solid #cbd5e1; border-radius: 1rem; box-shadow: 0 8px 32px #10234a26; }
.cookie-banner h2 { font-size: 1.125rem; font-weight: 600; }
.cookie-banner p, .cookie-preferences p { font-size: .9375rem; line-height: 1.5; margin: .5rem 0 1rem; }
.cookie-actions { display: flex; flex-wrap: wrap; gap: .65rem; justify-content: flex-end; }
.cookie-actions button, .cookie-error button { min-height: 44px; padding: .6rem .85rem; border: 1px solid #10234a; border-radius: .65rem; background: #fff; color: #10234a; font-weight: 600; }
.cookie-actions button:disabled { opacity: .6; cursor: wait; }
.cookie-actions button:focus-visible, .cookie-reopen:focus-visible, input:focus-visible { outline: 3px solid #0c8190; outline-offset: 3px; }
.cookie-category { border-top: 1px solid #d9e2ee; padding-top: 1rem; }
.cookie-category label { display: flex; align-items: center; gap: .5rem; font-weight: 600; min-height: 44px; }
.cookie-category input { width: 1.25rem; height: 1.25rem; accent-color: #10234a; }
.cookie-preferences a { text-decoration: underline; }
.cookie-reopen { position: fixed; bottom: .4rem; left: .5rem; z-index: 1000; min-height: 36px; padding: .3rem .6rem; border: 1px solid #cbd5e1; border-radius: .5rem; background: #fff; color: #10234a; font-size: .75rem; }
@media (max-width: 600px) { .cookie-actions { display: grid; grid-template-columns: 1fr; width: 100%; } .cookie-actions button { width: 100%; } }
</style>
